/**
 * Admin Portal → Codes → Issue codes → Coach Cedar — `/admin/codes/issue/house` (Codes PR-8 moved it from
 * `/admin/codes/house`, which redirects; Codes PR-4, migration 1876;
 * docs/architecture/38-PARTNER-ATTRIBUTION.md §16).
 *
 * Coach Cedar's own codes: discounts, longer free trials or tracking only, for in-house social
 * campaigns (codes sent by DM) and manual grants. They live on ONE reserved partner, the house
 * partner "Coach Cedar", so this page is that partner's detail page in HOUSE MODE (Overview,
 * Links & tags, Codes — no API keys, statements or settings; no bounty anywhere). A new tag goes
 * through `POST /api/admin/codes/house/tags`; its offer and codes through the partner endpoints,
 * with the batch's purpose and notes. Writes need an admin role; reads are open to every role.
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminCard, SectionHeader } from '../../../components/admin/ui';
import { AdminPartnerDetail } from '../partners/AdminPartnerDetail';
import { NoticeLine, errText, hint } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import type { HouseOverview } from './codesAdminLogic';
import { CODES_ROUTES } from './codesAreaLogic';

export function AdminHouseCodes() {
  const [overview, setOverview] = useState<HouseOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    CodesAdmin.house()
      .then((r) => { if (live) setOverview(r); })
      .catch((e) => { if (live) setError(errText(e)); });
    return () => { live = false; };
  }, []);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <SectionHeader title="Coach Cedar codes" />
        <NoticeLine notice={{ kind: 'error', text: error }} />
      </div>
    );
  }
  if (!overview) return <div style={{ padding: 24 }}>Loading Coach Cedar codes…</div>;
  if (!overview.house) {
    return (
      <div style={{ padding: 24 }}>
        <SectionHeader title="Coach Cedar codes" />
        <AdminCard>
          <div data-testid="house-missing">
            Coach Cedar codes aren&apos;t set up yet — the deploy may still be finishing. Try again in a few minutes.
          </div>
        </AdminCard>
      </div>
    );
  }
  return (
    <div>
      <div style={{ padding: '16px 24px 0', maxWidth: 1280 }}>
        {!overview.house_codes_enabled && (
          <div data-testid="house-codes-off" style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
            Coach Cedar codes are switched off (FF_HOUSE_CODES): no house tag, offer or code can change, and house codes
            read as unknown codes.
          </div>
        )}
        {overview.house_codes_enabled && !overview.redemption_live && (
          <div data-testid="house-codes-dark" style={hint}>
            The partner program is dark (FF_PARTNER_ATTRIBUTION off): codes made here are ready, and redeem once the program
            is live (or for a listed test account).
          </div>
        )}
        <div style={hint}>Every code made here is one batch: see <Link to={`${CODES_ROUTES.batches}?owner=house`}>Code batches</Link>.</div>
      </div>
      <AdminPartnerDetail partnerId={overview.house.id} />
    </div>
  );
}

export default AdminHouseCodes;
